/* ==========================================================================
   <sticky-columns> custom element
   Two-column product layout where the shorter column stays pinned while the
   taller column keeps scrolling. When the taller column ends, the whole page
   continues to scroll into the next section.

   - Columns shorter than the viewport stick below the header + offset.
   - Columns taller than the viewport scroll until their bottom edge is
     visible, then stick, so no content is ever hidden.
   - Follows a sticky / hide-on-scroll header automatically.
   - Disabled below the desktop breakpoint (stacked mobile layout).
   ========================================================================== */

if (!customElements.get('sticky-columns')) {
  class StickyColumns extends HTMLElement {
    constructor() {
      super();
      this.frame = null;
      this.trailing = null;
      this.columns = [];
      this.request = this.request.bind(this);
    }

    connectedCallback() {
      this.columns = Array.from(this.querySelectorAll(':scope > [data-sticky-column]'));
      if (this.columns.length === 0) return;

      this.offset = parseInt(this.dataset.offset, 10) || 0;
      this.bottomGap = parseInt(this.dataset.bottomGap, 10) || 24;
      this.headerAware = this.dataset.headerAware === 'true';
      this.media = window.matchMedia('(min-width: ' + (parseInt(this.dataset.breakpoint, 10) || 750) + 'px)');
      this.header = this.headerAware ? document.querySelector('.header-wrapper, [data-header]') : null;

      window.addEventListener('scroll', this.request, { passive: true });
      window.addEventListener('resize', this.request, { passive: true });
      window.addEventListener('load', this.request);

      if (this.media.addEventListener) {
        this.media.addEventListener('change', this.request);
      } else if (this.media.addListener) {
        this.media.addListener(this.request);
      }

      if (typeof ResizeObserver === 'function') {
        this.resizeObserver = new ResizeObserver(this.request);
        this.columns.forEach((column) => this.resizeObserver.observe(column));
      }

      if (this.header) {
        this.header.addEventListener('transitionend', this.request);
      }

      this.update();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.request);
      window.removeEventListener('resize', this.request);
      window.removeEventListener('load', this.request);

      if (this.media) {
        if (this.media.removeEventListener) {
          this.media.removeEventListener('change', this.request);
        } else if (this.media.removeListener) {
          this.media.removeListener(this.request);
        }
      }

      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.header) this.header.removeEventListener('transitionend', this.request);
      if (this.frame) window.cancelAnimationFrame(this.frame);
      if (this.trailing) window.clearTimeout(this.trailing);
    }

    request() {
      if (!this.frame) {
        this.frame = window.requestAnimationFrame(() => {
          this.frame = null;
          this.update();
        });
      }

      /* One trailing pass catches header show/hide animations that finish
         after the last scroll event. */
      if (this.trailing) window.clearTimeout(this.trailing);
      this.trailing = window.setTimeout(() => this.update(), 360);
    }

    headerBottom() {
      if (!this.header) return 0;
      const rect = this.header.getBoundingClientRect();
      return Math.max(0, Math.round(rect.bottom));
    }

    update() {
      if (!this.media || !this.media.matches) {
        this.classList.remove('is-sticky-ready');
        this.columns.forEach((column) => column.style.removeProperty('--sticky-top'));
        return;
      }

      const base = this.headerBottom() + this.offset;
      const viewport = window.innerHeight;

      this.columns.forEach((column) => {
        const height = column.offsetHeight;
        let top = base;

        if (height + base + this.bottomGap > viewport) {
          top = Math.min(base, viewport - height - this.bottomGap);
        }

        column.style.setProperty('--sticky-top', top + 'px');
      });

      this.classList.add('is-sticky-ready');
    }
  }

  if (!window.customElements.get('sticky-columns')) {
    window.customElements.define('sticky-columns', StickyColumns);
  }
}
